import { hasPageAfter, IPage, IPagedQueryOptions } from '../library/models/page';
import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { config as appConfig } from "../config/environment";
import { attachSession } from "./sessionKeeper";

export class BaseService {
    protected api: AxiosInstance;

    constructor() {
        this.api = axios.create({
            // Same source as ApiService, so both layers always agree on the host.
            baseURL: appConfig.apiBaseUrl,
            headers: {
                'Content-Type': 'application/json',
            },
        });

        attachSession(this.api);
    }

    protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response: AxiosResponse<T> = await this.api.get(url, config);
        return response.data;
    }

    protected async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response: AxiosResponse<T> = await this.api.post(url, data, config);
        return response.data;
    }

    protected async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response: AxiosResponse<T> = await this.api.put(url, data, config);
        return response.data;
    }

    protected async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response: AxiosResponse<T> = await this.api.patch(url, data, config);
        return response.data;
    }

    protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response: AxiosResponse<T> = await this.api.delete(url, config);
        return response.data;
    }

    protected createGetQuery = <TData = unknown>(url: string, data?: Record<string, any>): UseQueryOptions<TData> => {
        const queryKey = [url, data];

        const queryFn = async (): Promise<TData> => {
            const response = await this.api.get<TData>(url, { params: data });
            return response.data;
        };

        return {
            queryKey,
            queryFn,
        };
    };


    /**
     * A paged endpoint, as options for `useReactInfiniteQuery`.
     *
     * The same bargain as `createGetQuery` -- the service names the URL and the component
     * fires nothing itself -- with the page number supplied by React Query rather than by
     * the caller. `size` is part of the query key because two screens reading the same
     * list through different windows are not looking at the same cached thing.
     *
     * Never reach for this and then slice: the point of a paged endpoint is that the app
     * only ever holds the pages it has actually asked for.
     */
    protected createGetInfiniteQuery = <TItem = unknown>(url: string, size: number, data?: Record<string, any>): IPagedQueryOptions<TItem> => ({
        queryKey: [url, { ...data, size }] as readonly unknown[],
        queryFn: async ({ pageParam }: { pageParam: number }): Promise<IPage<TItem>> => {
            const response = await this.api.get<IPage<TItem>>(url, {
                params: { ...data, page: pageParam, size },
            });
            return response.data;
        },
        initialPageParam: 0,
        // undefined is how React Query is told there is no next page, which is what
        // `hasNextPage` reports back to the button.
        getNextPageParam: (lastPage: IPage<TItem>): number | undefined =>
            hasPageAfter(lastPage) ? lastPage.page + 1 : undefined,
    });

    protected createPostQuery = <TData = unknown>(url: string, data?: Record<string, any>): UseQueryOptions<TData> => {
        const queryKey = [url, data];

        const queryFn = async (): Promise<TData> => {
            const response = await this.api.post<TData>(url, data);
            return response.data;
        };

        return {
            queryKey,
            queryFn,
        };
    };

    protected createMutation = <TData = unknown, TVariables = unknown>(url: string, method: "post" | "put" | "patch" | "delete" = "post"): UseMutationOptions<TData, unknown, TVariables> => {
        const mutationFn = async (variables: TVariables): Promise<TData> => {
            const finalUrl =
                method === "delete"
                    ? `${url}?id=${variables}`
                    : url;

            const response = await this.api.request<TData>({
                url: finalUrl,
                method,
                data: method === "delete" ? undefined : variables,
            });

            return response.data;
        };

        return {
            mutationFn,
        };
    };
}
